import { useTranslation } from '../../../i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Tabs, Table, Empty, Spin, Alert } from 'antd';
import { Line, Column } from '@ant-design/plots';
import hostSharedService from '../../../../shared/services/hostSharedService';

const { TabPane } = Tabs;

const FlowStatistics = ({ vhostName }) => {
    const { t } = useTranslation();
    const [activeType, setActiveType] = useState('day');
    const [loading, setLoading] = useState(false);
    const [flowData, setFlowData] = useState({ flows: [], unit: '', period: '' });

    useEffect(() => {
        if (vhostName) {
            loadData(activeType);
        } else {
            setFlowData({ flows: [], unit: '', period: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vhostName, activeType]);

    const loadData = async (type) => {
        if (!vhostName) return;
        setLoading(true);
        try {
            const response = await hostSharedService.viewFlow(vhostName, type);
            console.log('[FlowStatistics] API response:', response);
            if (response.success) {
                const data = response.data || { flows: [], unit: '', period: '' };
                console.log('[FlowStatistics] Setting flowData:', data);
                setFlowData(data);
            } else {
                console.log('[FlowStatistics] API returned success=false');
                setFlowData({ flows: [], unit: '', period: '' });
            }
        } catch (error) {
            console.error('[FlowStatistics] Error loading data:', error);
            setFlowData({ flows: [], unit: '', period: '' });
        } finally {
            setLoading(false);
        }
    };

    const chartData = useMemo(() => {
        if (!Array.isArray(flowData.flows)) {
            console.log('[FlowStatistics] flowData.flows is not an array:', flowData.flows);
            return [];
        }
        const data = flowData.flows.flatMap((item) => [
            { time: item.time, value: item.flow || 0, category: t('Total traffic (MB)') },
            { time: item.time, value: item.flow_cache || 0, category: t('Cached traffic (MB)') }
        ]);
        console.log('[FlowStatistics] chartData:', data);
        return data;
    }, [flowData, t]);

    const columns = [
        { title: t('Time'), dataIndex: 'time', key: 'time' },
        { title: t('Total traffic (MB)'), dataIndex: 'flow', key: 'flow', render: (v) => v?.toLocaleString() || 0 },
        { title: t('Cached traffic (MB)'), dataIndex: 'flow_cache', key: 'flow_cache', render: (v) => v?.toLocaleString() || 0 }
    ];

    // 当数据点少于2个时使用柱状图，否则使用线图
    const useColumnChart = flowData.flows?.length < 2;

    const lineConfig = {
        data: chartData,
        xField: 'time',
        yField: 'value',
        seriesField: 'category',
        smooth: true,
        autoFit: true,
        height: 300,
        tooltip: {
            showMarkers: true
        },
        legend: {
            position: 'top'
        }
    };

    const columnConfig = {
        data: chartData,
        xField: 'time',
        yField: 'value',
        seriesField: 'category',
        isGroup: true,
        autoFit: true,
        height: 300,
        tooltip: {
            showMarkers: true
        },
        legend: {
            position: 'top'
        }
    };

    const hasFlowData = Array.isArray(flowData.flows) && flowData.flows.length > 0;

    return (
        <Card title={t('Traffic statistics')}>
            <Alert
                message={t('Description')}
                description={t('View real-time traffic and cache hits for your shared hosting, with daily, monthly, or yearly views.')}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <Tabs activeKey={activeType} onChange={setActiveType}>
                <TabPane tab={t('24 hours')} key="day" />
                <TabPane tab={t('30 days')} key="month" />
                <TabPane tab={t('12 months')} key="year" />
            </Tabs>

            {loading ? (
                <Spin />
            ) : !hasFlowData ? (
                <Empty description={t('No traffic data yet')} />
            ) : (
                <div style={{ marginBottom: 24 }}>
                    {useColumnChart ? (
                        <Column {...columnConfig} />
                    ) : (
                        <Line {...lineConfig} />
                    )}
                </div>
            )}

            <Table
                rowKey={(record, index) => `${record.time}-${index}`}
                columns={columns}
                dataSource={flowData.flows || []}
                pagination={false}
            />
        </Card>
    );
};

export default FlowStatistics;
